import { FC, PropsWithChildren, ReactNode } from 'react';

import { useFeatureFlag, usePermission } from '../hooks';
import { Permission } from '../lib';

interface ProtectedContentWithPermissionOrFeatureFlagProps extends PropsWithChildren {
	fallback?: ReactNode;
	permissions?: Permission[] | Permission;
	flag: string | string[];
}

/*
 * The user needs either a feature flag on to render the children,
 * or they need the corresponding permission to do so. This is a disjunctive (OR), not
 * conjunctive (AND). If none is true, invoke the fallback.
 *
 * Note, if don't need to conditionally render content based on one or the other, use:
 *  A) FeatureFlag component if you only need to check for a feature flag
 *  B) ProtectedContent component if you only need to check for permissions
 */
export const ProtectedContentWithPermissionOrFeatureFlag: FC<ProtectedContentWithPermissionOrFeatureFlagProps> = ({
	children,
	fallback,
	permissions,
	flag,
}) => {
	const hasPermission = usePermission(permissions);
	const flagEnabled = useFeatureFlag(flag);
	const isFlagEnabled = Array.isArray(flagEnabled) ? flagEnabled.some(Boolean) : flagEnabled;

	if (hasPermission || isFlagEnabled) {
		return children;
	} else {
		return fallback || null;
	}
};
